import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  bulkUpdatePricing,
  getAllServices,
  getAllConsultationModes,
} from "../../../api/services/api";

interface BulkPricingFormProps {
  toggleDrawer: (open: boolean) => void;
}

export const BulkPricingForm = ({ toggleDrawer }: BulkPricingFormProps) => {
  const queryClient = useQueryClient();
  const [serviceId, setServiceId] = useState("");
  const [consultationModeId, setConsultationModeId] = useState("");
  const [price, setPrice] = useState("");
  const [updateType, setUpdateType] = useState<"percentage" | "fixed">("fixed");
  const [percentageChange, setPercentageChange] = useState("");
  const [applyToAll, setApplyToAll] = useState(false);

  const { data: services } = useQuery(
    ["services", { limit: 1000 }],
    getAllServices
  );
  const { data: modes } = useQuery(
    ["consultation-modes", { limit: 1000 }],
    getAllConsultationModes
  );

  const { mutate: bulkUpdateMutate, isLoading } = useMutation(
    bulkUpdatePricing,
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("pricing");
        queryClient.invalidateQueries("pricing-stats");
        toast.success(
          res?.data?.message || "Bulk pricing updated successfully"
        );
        toggleDrawer(false);
        resetForm();
      },
      onError: (res: any) => {
        toast.error(res?.response?.data?.message || "Failed to update pricing");
      },
    }
  );

  const resetForm = () => {
    setServiceId("");
    setConsultationModeId("");
    setPrice("");
    setPercentageChange("");
    setApplyToAll(false);
  };

  const onSubmit = () => {
    const payload: any = {
      filters: {},
      update: {},
    };

    if (!applyToAll) {
      if (serviceId) {
        payload.filters.serviceId = parseInt(serviceId);
      }
      if (consultationModeId) {
        payload.filters.consultationModeId = parseInt(consultationModeId);
      }
    }

    if (updateType === "fixed" && price) {
      payload.update.price = parseFloat(price);
    } else if (updateType === "percentage" && percentageChange) {
      payload.update.percentageChange = parseFloat(percentageChange);
    }

    bulkUpdateMutate(payload);
  };

  return (
    <Box width="450px">
      <Typography variant="h6" mb={2}>
        Bulk Update Pricing
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={applyToAll}
            onChange={(e) => setApplyToAll(e.target.checked)}
          />
        }
        label="Apply to all configurations"
      />

      {!applyToAll && (
        <>
          <TextField
            sx={{ mt: 2 }}
            fullWidth
            select
            label="Service (Optional)"
            variant="standard"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
          >
            <MenuItem value="">All Services</MenuItem>
            {services?.data?.data?.map((service: any) => (
              <MenuItem key={service.id} value={service.id}>
                {service.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            sx={{ mt: 2 }}
            fullWidth
            select
            label="Consultation Mode (Optional)"
            variant="standard"
            value={consultationModeId}
            onChange={(e) => setConsultationModeId(e.target.value)}
          >
            <MenuItem value="">All Modes</MenuItem>
            {modes?.data?.data?.map((mode: any) => (
              <MenuItem key={mode.id} value={mode.id}>
                {mode.name}
              </MenuItem>
            ))}
          </TextField>
        </>
      )}

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        select
        label="Update Type"
        variant="standard"
        value={updateType}
        onChange={(e) =>
          setUpdateType(e.target.value as "percentage" | "fixed")
        }
        required
      >
        <MenuItem value="fixed">Set Fixed Price</MenuItem>
        <MenuItem value="percentage">Percentage Change</MenuItem>
      </TextField>

      {updateType === "fixed" ? (
        <TextField
          sx={{ mt: 2, mb: 3 }}
          fullWidth
          type="number"
          label="New Price (SAR)"
          variant="standard"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          inputProps={{ min: 0, step: 0.01 }}
        />
      ) : (
        <TextField
          sx={{ mt: 2, mb: 3 }}
          fullWidth
          type="number"
          label="Percentage Change (%)"
          variant="standard"
          value={percentageChange}
          onChange={(e) => setPercentageChange(e.target.value)}
          required
          helperText="Positive for increase, negative for decrease"
          inputProps={{ step: 0.1 }}
        />
      )}

      <Button
        type="submit"
        onClick={onSubmit}
        variant="contained"
        fullWidth
        disabled={
          isLoading ||
          (updateType === "fixed" && !price) ||
          (updateType === "percentage" && !percentageChange)
        }
      >
        {isLoading ? "Updating..." : "Apply Bulk Update"}
      </Button>
    </Box>
  );
};
